import { Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ChartColumnBig, CircleUserRound, HandHeart, House, MessageCircle, type LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { threadsQuery } from '@/lib/queries'

/* Spec PWA-001 — FR-034: navegacao inferior do design do TR: Inicio, Mensagens, [Doar] em destaque, Impacto, Conta.
   "Doar" e o botao central elevado (amarelo de destaque, token accent); as demais abas marcam a ativa com a cor da marca. */

type TabPath = '/inicio' | '/mensagens' | '/impacto' | '/conta'
type Tab = { to: TabPath; labelKey: string; Icon: LucideIcon; exact: boolean }

const LEFT: Tab[] = [
    { to: '/inicio', labelKey: 'sponsorShell.nav.home', Icon: House, exact: true },
    { to: '/mensagens', labelKey: 'sponsorShell.nav.messages', Icon: MessageCircle, exact: false },
]
const RIGHT: Tab[] = [
    { to: '/impacto', labelKey: 'sponsorShell.nav.impact', Icon: ChartColumnBig, exact: true },
    { to: '/conta', labelKey: 'sponsorShell.nav.account', Icon: CircleUserRound, exact: true },
]

function TabLink({ tab, unread }: { tab: Tab; unread: number }) {
    const { t } = useTranslation()
    const { to, labelKey, Icon, exact } = tab
    const showBadge = to === '/mensagens' && unread > 0
    return (
        <Link
            to={to}
            activeOptions={{ exact }}
            className="relative flex min-h-[3.6rem] flex-col items-center justify-center gap-1 rounded-xl text-[0.66rem] font-semibold text-ink-muted no-underline data-[status=active]:font-bold data-[status=active]:text-brand"
        >
            {({ isActive }) => (
                <>
                    <span className="relative">
                        <Icon className="size-6" strokeWidth={isActive ? 2.3 : 1.9} aria-hidden="true" />
                        {showBadge ? (
                            <span aria-hidden="true" className="absolute -top-1.5 -right-2.5 flex min-w-[1.1rem] items-center justify-center rounded-full bg-action px-1 text-[0.62rem] leading-[1.1rem] font-bold text-ink">
                                {unread}
                            </span>
                        ) : null}
                    </span>
                    <span>{t(labelKey)}</span>
                    {showBadge ? <span className="sr-only">{t('sponsorShell.nav.unread', { count: unread })}</span> : null}
                </>
            )}
        </Link>
    )
}

export function BottomNav() {
    const { t } = useTranslation()
    const threads = useQuery(threadsQuery)
    const unread = threads.data?.reduce((n, th) => n + th.unreadCount, 0) ?? 0

    return (
        <nav aria-label={t('sponsorShell.nav.label')} className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-surface pb-safe-bottom lg:hidden">
            <ul className="mx-auto m-0 grid max-w-lg list-none grid-cols-5 items-end p-0 px-1.5 py-2">
                {LEFT.map(tab => <li key={tab.to}><TabLink tab={tab} unread={unread} /></li>)}
                <li className="flex justify-center">
                    <Link
                        to="/doar"
                        className="group -mt-7 flex flex-col items-center gap-1 text-[0.66rem] font-bold text-ink no-underline"
                    >
                        <span className="flex size-15 items-center justify-center rounded-full bg-accent text-ink shadow-[0_6px_16px_-4px_rgb(0_0_0/0.35)] ring-4 ring-surface transition-transform group-hover:scale-105 group-data-[status=active]:ring-brand-soft">
                            <HandHeart className="size-7" strokeWidth={2.1} aria-hidden="true" />
                        </span>
                        <span>{t('sponsorShell.nav.donate')}</span>
                    </Link>
                </li>
                {RIGHT.map(tab => <li key={tab.to}><TabLink tab={tab} unread={unread} /></li>)}
            </ul>
        </nav>
    )
}
